import React, { useState } from "react";
import { Box } from "@mui/material";
import { NavLink } from "react-router-dom";
import useReveal from "./useReveal";
import PageHero from "./PageHero";
import { PLANS, COMPARISON, PRICING_FAQ, perMonth } from "./siteContent";
import { IconCheck, IconArrow, IconShield, IconUsers } from "../Home/landingArt";
import { IconLifebuoy, IconSpark } from "./marketingArt";
import "../Home/landingpage.scss";
import "./marketing.scss";

/* Included on every plan — stated once, so the plan cards stay readable. */
const INCLUDED = [
  {
    icon: <IconSpark />,
    tone: "is-green",
    title: "The whole product",
    body: "Board, backlog, sprints, epics, estimates, burn-down and reports. No core capability is held back for a higher tier.",
  },
  {
    icon: <IconUsers />,
    tone: "is-blue",
    title: "Unlimited projects",
    body: "Run as many projects and sprints as you need. Plans are limited by people, not by how much work you track.",
  },
  {
    icon: <IconShield />,
    tone: "is-violet",
    title: "Your data stays yours",
    body: "Your projects, issues and history belong to your organisation, and we will export them for you on request.",
  },
  {
    icon: <IconLifebuoy />,
    tone: "is-amber",
    title: "Support included",
    body: "Every plan comes with support from the people who build Projam. Higher plans get a priority queue.",
  },
];

const Cell = ({ value }) => {
  if (value === true) {
    return (
      <span className="pg-yes" aria-label="Included">
        <IconCheck />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="pg-no" aria-label="Not included">
        –
      </span>
    );
  }
  return <>{value}</>;
};

const Pricing = () => {
  const rootRef = useReveal();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <Box className="pj-landing pj-page" ref={rootRef}>
      <PageHero
        eyebrow="Pricing"
        title={
          <>
            Priced by team size,{" "}
            <span className="lp-gradient-text">not by feature</span>
          </>
        }
        lead="Run the whole product free for 60 days, then pick the plan that covers your team. Every plan includes the same features — nothing is locked away behind a tier."
      >
        <NavLink to="/register" className="lp-btn lp-btn--primary">
          Start 60 days free <IconArrow />
        </NavLink>
        <NavLink to="/contact" className="lp-btn lp-btn--ghost">
          Talk to sales
        </NavLink>
      </PageHero>

      {/* ----------------------------------------------------------- plans */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-head" style={{ marginBottom: 40 }}>
            <span className="lp-eyebrow" data-reveal>
              Plans
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              Start free, then pick your plan
            </h2>
            <div className="pg-toggle-note" data-reveal data-reveal-delay="140">
              Try the whole product for <b>60 days, free</b> — no credit card,
              nothing switched off.
            </div>
          </div>

          <div className="pg-plans">
            {PLANS.map((plan, i) => (
              <article
                className={`pg-plan ${plan.popular ? "is-popular" : ""}`}
                key={plan.id}
                data-reveal
                data-reveal-delay={i * 90}
              >
                {plan.popular ? (
                  <span className="pg-plan-flag">Most popular</span>
                ) : null}

                <h3 className="pg-plan-name">{plan.name}</h3>
                <p className="pg-plan-tagline">{plan.tagline}</p>

                {plan.free ? (
                  <>
                    <div className="pg-plan-price">
                      <b>Free</b>
                      <span>for {plan.trialDays} days</span>
                    </div>
                    <p className="pg-plan-billing">
                      <b>No credit card required</b> — the complete product,
                      for up to {plan.users} users, for the full{" "}
                      {plan.trialDays} days.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="pg-plan-price">
                      <b>${perMonth(plan)}</b>
                      <span>/ month</span>
                    </div>
                    <p className="pg-plan-billing">
                      <b>{plan.billingNote}</b> — ${plan.price} per{" "}
                      {plan.termMonths === 1
                        ? "month"
                        : `${plan.termMonths} months`}
                      , for up to {plan.users} users.
                    </p>
                  </>
                )}

                <div className="pg-plan-cta">
                  <NavLink
                    to={plan.ctaTo}
                    className={`lp-btn ${
                      plan.popular ? "lp-btn--primary" : "lp-btn--ghost"
                    }`}
                  >
                    {plan.ctaLabel}
                  </NavLink>
                </div>

                <p className="pg-plan-best">
                  <b>Best for:</b> {plan.best}
                </p>

                <ul className="pg-plan-list">
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <span className="lp-check">
                        <IconCheck />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <p className="pg-table-note" data-reveal>
            Prices are in USD and exclude any local taxes. Need more than{" "}
            {PLANS[PLANS.length - 1].users} users, a longer term or specific
            security arrangements?{" "}
            <NavLink to="/contact" className="pg-inline-link">
              Talk to us
            </NavLink>{" "}
            and we will put a proposal together.
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------- included */}
      <section className="lp-section pg-band" style={{ paddingTop: 72 }}>
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              On every plan
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              What you get whichever you choose
            </h2>
          </div>

          <div className="lp-grid pg-grid-4">
            {INCLUDED.map((item, i) => (
              <article
                className="lp-card"
                key={item.title}
                data-reveal
                data-reveal-delay={i * 70}
              >
                <div className={`lp-card-icon ${item.tone}`}>{item.icon}</div>
                <h3 className="lp-h3">{item.title}</h3>
                <p className="lp-body">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ comparison */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              Compare
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              Plan by plan, line by line
            </h2>
          </div>

          <p className="pg-scroll-hint" data-reveal>
            Scroll the table sideways to compare all three plans
            <IconArrow />
          </p>

          <div className="pg-table-wrap" data-reveal>
            <div className="pg-table-scroll">
              <table className="pg-table">
                <caption className="sr-only" style={{ display: "none" }}>
                  Feature comparison across the Free trial, Basic and Gold plans
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    {PLANS.map((plan) => (
                      <th scope="col" key={plan.id}>
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((group) => (
                    <React.Fragment key={group.group}>
                      <tr className="pg-table-group">
                        <th scope="colgroup" colSpan={PLANS.length + 1}>
                          {group.group}
                        </th>
                      </tr>
                      {group.rows.map((row) => (
                        <tr key={row.label}>
                          <th scope="row">{row.label}</th>
                          {row.values.map((value, i) => (
                            <td key={PLANS[i].id}>
                              <Cell value={value} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="pg-table-note" data-reveal>
            The 60-day trial is ticked wherever the product itself is — that is
            what full access means. Support levels are the one thing reserved
            for a paid plan.
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------- faq */}
      <section className="lp-section pg-band">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              Questions
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              Before you decide
            </h2>
          </div>

          <div className="pg-faq">
            {PRICING_FAQ.map((item, i) => (
              <div
                className={`pg-faq-item ${openFaq === i ? "is-open" : ""}`}
                key={item.q}
                data-reveal
                data-reveal-delay={(i % 2) * 70}
              >
                <button
                  type="button"
                  className="pg-faq-q"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  {item.q}
                  <i aria-hidden="true" />
                </button>
                <div className="pg-faq-a">
                  <div>
                    <p>{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-cta" data-reveal>
            <h2 className="lp-h2">Still weighing it up?</h2>
            <p>
              Take the full 60 days and see how your team's own sprint runs —
              or tell us about them and we will recommend a plan.
            </p>
            <div className="lp-cta-actions">
              <NavLink to="/register" className="lp-btn lp-btn--white">
                Start 60 days free <IconArrow />
              </NavLink>
              <NavLink to="/contact" className="lp-btn lp-btn--light">
                Talk to sales
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default Pricing;
